import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-background">
      <div className="w-full max-w-sm animate-fade-in">
        <img src="/sterlingtask-logo.png?v=3" alt="SterlingTask" className="h-20 w-auto object-contain mb-8" />
        <div className="font-serif text-4xl tracking-tight">{title}</div>
        <p className="text-muted-foreground mt-3 text-sm">{sub}</p>
        <div className="mt-8">{children}</div>
        <Link to="/login" data-testid="back-to-login" className="mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-brand transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Forgot password" sub="Enter your login email and we'll send you a secure link to set a new password.">
      {msg ? (
        <div className="flex gap-3 rounded-xl border border-brand/30 bg-brand/5 p-4 text-sm" data-testid="forgot-sent"><CheckCircle2 className="w-5 h-5 text-brand shrink-0" />{msg}</div>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-2">
            <Label className="label-caps text-muted-foreground">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" placeholder="you@thesterlingtouch.com.au" />
          </div>
          {error && <p className="text-destructive text-sm" data-testid="forgot-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-sm" data-testid="forgot-submit">{busy ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [f, setF] = useState({ next: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (f.next.length < 8) return setError("Password must be at least 8 characters");
    if (f.next !== f.confirm) return setError("The passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: f.next });
      setDone(data.message);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const welcome = params.get("welcome") === "1";
  if (!token) return <Shell title="Link missing" sub="This reset link is incomplete. Request a new one from the sign-in page." />;
  return (
    <Shell title={welcome ? "Welcome to SterlingTask" : "Set a new password"}
      sub={welcome ? "Choose a password (at least 8 characters) to finish setting up your account." : "At least 8 characters. Other devices will be signed out."}>
      {done ? (
        <div className="space-y-4" data-testid="reset-done">
          <div className="flex gap-3 rounded-xl border border-brand/30 bg-brand/5 p-4 text-sm"><CheckCircle2 className="w-5 h-5 text-brand shrink-0" />{done}</div>
          <Button asChild className="w-full rounded-sm"><Link to="/login" data-testid="reset-go-login">Sign in</Link></Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">New password</Label>
            <Input type="password" autoComplete="new-password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} data-testid="reset-new" /></div>
          <div className="space-y-2"><Label className="label-caps text-muted-foreground">Confirm new password</Label>
            <Input type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="reset-confirm" /></div>
          {error && <p className="text-destructive text-sm" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full rounded-sm" data-testid="reset-submit">{busy ? "Saving…" : "Save new password"}</Button>
        </form>
      )}
    </Shell>
  );
}
